import React, { useEffect, useState } from 'react';
import { attendanceAPI, employeesAPI } from '../services/api';
import toast from 'react-hot-toast';
import {
  Settings as SettingsIcon,
  Clock,
  Building,
  Briefcase,
  Plus,
  Edit2,
  Trash2,
  X,
  ToggleLeft,
  ToggleRight
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const Settings = () => {
  const [shifts, setShifts] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [positions, setPositions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('shifts');

  const [shiftForm, setShiftForm] = useState({ name: '', startTime: '09:00', endTime: '17:00', lateGraceMinutes: 15, isActive: true });
  const [deptForm, setDeptForm] = useState({ name: '', description: '', isActive: true });
  const [posForm, setPosForm] = useState({ name: '', description: '', isActive: true });

  const [showEditModal, setShowEditModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [editType, setEditType] = useState('');

  const load = async () => {
    try {
      setIsLoading(true);
      const [shiftsRes, deptsRes, posRes] = await Promise.all([
        attendanceAPI.listShifts(),
        employeesAPI.listDepartments(),
        employeesAPI.listPositions()
      ]);
      setShifts(shiftsRes.data || []);
      setDepartments(deptsRes.data || []);
      setPositions(posRes.data || []);
    } catch (e) {
      toast.error('Failed to load settings');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  // Shift handlers
  const handleShiftChange = (e) => {
    const { name, value, type, checked } = e.target;
    setShiftForm(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const createShift = async (e) => {
    e.preventDefault();
    try {
      await attendanceAPI.createShift(shiftForm);
      toast.success('Shift created');
      setShiftForm({ name: '', startTime: '09:00', endTime: '17:00', lateGraceMinutes: 15, isActive: true });
      load();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to create shift');
    }
  };

  const updateShift = async (id, data) => {
    try {
      await attendanceAPI.updateShift(id, data);
      toast.success('Shift updated');
      load();
      setShowEditModal(false);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to update shift');
    }
  };

  const deleteShift = async (id) => {
    if (!window.confirm('Delete this shift?')) return;
    try {
      await attendanceAPI.deleteShift(id);
      toast.success('Shift deleted');
      load();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to delete shift');
    }
  };

  // Department handlers
  const handleDeptChange = (e) => {
    const { name, value, type, checked } = e.target;
    setDeptForm(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const createDepartment = async (e) => {
    e.preventDefault();
    try {
      await employeesAPI.createDepartment(deptForm);
      toast.success('Department created');
      setDeptForm({ name: '', description: '', isActive: true });
      load();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to create department');
    }
  };

  const updateDepartment = async (id, data) => {
    try {
      await employeesAPI.updateDepartment(id, data);
      toast.success('Department updated');
      load();
      setShowEditModal(false);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to update department');
    }
  };

  const deleteDepartment = async (id) => {
    if (!window.confirm('Delete this department?')) return;
    try {
      await employeesAPI.deleteDepartment(id);
      toast.success('Department deleted');
      load();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to delete department');
    }
  };

  // Position handlers
  const handlePosChange = (e) => {
    const { name, value, type, checked } = e.target;
    setPosForm(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const createPosition = async (e) => {
    e.preventDefault();
    try {
      await employeesAPI.createPosition(posForm);
      toast.success('Position created');
      setPosForm({ name: '', description: '', isActive: true });
      load();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to create position');
    }
  };

  const updatePosition = async (id, data) => {
    try {
      await employeesAPI.updatePosition(id, data);
      toast.success('Position updated');
      load();
      setShowEditModal(false);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to update position');
    }
  };

  const deletePosition = async (id) => {
    if (!window.confirm('Delete this position?')) return;
    try {
      await employeesAPI.deletePosition(id);
      toast.success('Position deleted');
      load();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to delete position');
    }
  };

  const openEditModal = (item, type) => {
    setEditingItem(item);
    setEditType(type);
    setShowEditModal(true);
  };

  const tabs = [
    { id: 'shifts', name: 'Shifts', icon: Clock, count: shifts.length, color: 'indigo' },
    { id: 'departments', name: 'Departments', icon: Building, count: departments.length, color: 'emerald' },
    { id: 'positions', name: 'Positions', icon: Briefcase, count: positions.length, color: 'amber' },
  ];

  if (isLoading) {
    return (
      <div className="py-16 flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-4 border-gray-200"></div>
      </div>
    );
  }

  return (
    <div className="">
      <div className=" space-y-6">

        {/* Header */}
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-gray-100 rounded-md flex items-center justify-center">
            <SettingsIcon className="w-5 h-5 text-gray-500" />
          </div>
          <div>
            <h1 className="text-lg font-semibold text-slate-800">Settings</h1>
            <p className="text-sm text-slate-500">Manage shifts, departments, and positions</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-1.5 flex gap-1">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-md font-medium text-sm transition-all ${activeTab === tab.id
                  ? 'bg-gray-900 text-white shadow-none'
                  : 'text-slate-600 hover:bg-slate-50'
                }`}
            >
              <tab.icon size={16} />
              {tab.name}
              <span className={`px-2 py-0.5 rounded-full text-xs ${activeTab === tab.id ? 'bg-white/20' : 'bg-slate-100'
                }`}>
                {tab.count}
              </span>
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">

          {/* Create Form */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Plus size={18} className="text-gray-700" />
                Create {activeTab === 'shifts' ? 'Shift' : activeTab === 'departments' ? 'Department' : 'Position'}
              </h2>

              {activeTab === 'shifts' && (
                <form onSubmit={createShift} className="space-y-4">
                  <FormInput label="Name" name="name" value={shiftForm.name} onChange={handleShiftChange} required />
                  <div className="grid grid-cols-2 gap-4">
                    <FormInput label="Start Time" name="startTime" type="time" value={shiftForm.startTime} onChange={handleShiftChange} required />
                    <FormInput label="End Time" name="endTime" type="time" value={shiftForm.endTime} onChange={handleShiftChange} required />
                  </div>
                  <FormInput label="Late Grace (mins)" name="lateGraceMinutes" type="number" value={shiftForm.lateGraceMinutes} onChange={handleShiftChange} />
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" name="isActive" checked={shiftForm.isActive} onChange={handleShiftChange} className="w-4 h-4 rounded text-gray-700" />
                    <span className="text-sm text-slate-700">Active</span>
                  </label>
                  <button type="submit" className="w-full py-2.5 bg-gray-900 hover:bg-gray-800 text-white font-semibold rounded-md transition-colors">
                    Create Shift
                  </button>
                </form>
              )}

              {activeTab === 'departments' && (
                <form onSubmit={createDepartment} className="space-y-4">
                  <FormInput label="Name" name="name" value={deptForm.name} onChange={handleDeptChange} required />
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1.5">Description</label>
                    <textarea name="description" value={deptForm.description} onChange={handleDeptChange} rows="3" className="w-full px-3 py-2.5 border border-slate-200 rounded-md focus:ring-2 focus:ring-gray-200 focus:border-transparent text-sm resize-none" />
                  </div>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" name="isActive" checked={deptForm.isActive} onChange={handleDeptChange} className="w-4 h-4 rounded text-gray-700" />
                    <span className="text-sm text-slate-700">Active</span>
                  </label>
                  <button type="submit" className="w-full py-2.5 bg-gray-900 hover:bg-gray-800 text-white font-semibold rounded-md transition-colors">
                    Create Department
                  </button>
                </form>
              )}

              {activeTab === 'positions' && (
                <form onSubmit={createPosition} className="space-y-4">
                  <FormInput label="Name" name="name" value={posForm.name} onChange={handlePosChange} required />
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1.5">Description</label>
                    <textarea name="description" value={posForm.description} onChange={handlePosChange} rows="3" className="w-full px-3 py-2.5 border border-slate-200 rounded-md focus:ring-2 focus:ring-gray-200 focus:border-transparent text-sm resize-none" />
                  </div>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" name="isActive" checked={posForm.isActive} onChange={handlePosChange} className="w-4 h-4 rounded text-gray-700" />
                    <span className="text-sm text-slate-700">Active</span>
                  </label>
                  <button type="submit" className="w-full py-2.5 bg-gray-100 hover:bg-gray-100 text-white font-semibold rounded-md transition-colors">
                    Create Position
                  </button>
                </form>
              )}
            </div>
          </div>

          {/* List */}
          <div className="lg:col-span-3">
            <div className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden">
              <div className="px-4 py-3 border-b border-slate-100 bg-slate-50/50">
                <h2 className="text-lg font-semibold text-gray-900">
                  {activeTab === 'shifts' ? 'All Shifts' : activeTab === 'departments' ? 'All Departments' : 'All Positions'}
                </h2>
              </div>

              <div className="divide-y divide-slate-50">
                {activeTab === 'shifts' && shifts.map(s => (
                  <ItemRow
                    key={s._id}
                    item={s}
                    title={s.name}
                    subtitle={`${s.startTime} - ${s.endTime} • Grace: ${s.lateGraceMinutes}m`}
                    isActive={s.isActive}
                    onToggle={() => updateShift(s._id, { isActive: !s.isActive })}
                    onEdit={() => openEditModal(s, 'shift')}
                    onDelete={() => deleteShift(s._id)}
                    color="indigo"
                  />
                ))}
                {activeTab === 'departments' && departments.map(d => (
                  <ItemRow
                    key={d._id}
                    item={d}
                    title={d.name}
                    subtitle={d.description || 'No description'}
                    isActive={d.isActive}
                    onToggle={() => updateDepartment(d._id, { isActive: !d.isActive })}
                    onEdit={() => openEditModal(d, 'department')}
                    onDelete={() => deleteDepartment(d._id)}
                    color="emerald"
                  />
                ))}
                {activeTab === 'positions' && positions.map(p => (
                  <ItemRow
                    key={p._id}
                    item={p}
                    title={p.name}
                    subtitle={p.description || 'No description'}
                    isActive={p.isActive}
                    onToggle={() => updatePosition(p._id, { isActive: !p.isActive })}
                    onEdit={() => openEditModal(p, 'position')}
                    onDelete={() => deletePosition(p._id)}
                    color="amber"
                  />
                ))}
                {((activeTab === 'shifts' && shifts.length === 0) ||
                  (activeTab === 'departments' && departments.length === 0) ||
                  (activeTab === 'positions' && positions.length === 0)) && (
                    <div className="p-12 text-center text-slate-400">
                      <SettingsIcon className="w-10 h-10 mx-auto mb-2 opacity-50" />
                      <p>No items yet</p>
                    </div>
                  )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Edit Modal */}
      <AnimatePresence>
        {showEditModal && editingItem && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm"
            onClick={() => setShowEditModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.95 }}
              className="bg-white w-full max-w-md rounded-lg shadow-2xl overflow-hidden"
              onClick={e => e.stopPropagation()}
            >
              <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between bg-slate-50">
                <h3 className="text-lg font-semibold text-gray-900">Edit {editType}</h3>
                <button onClick={() => setShowEditModal(false)} className="p-1.5 hover:bg-slate-200 rounded-full">
                  <X size={18} />
                </button>
              </div>

              <form onSubmit={(e) => {
                e.preventDefault();
                const formData = new FormData(e.target);
                if (editType === 'shift') {
                  updateShift(editingItem._id, {
                    name: formData.get('name'),
                    startTime: formData.get('startTime'),
                    endTime: formData.get('endTime'),
                    lateGraceMinutes: Number(formData.get('lateGraceMinutes')),
                    isActive: formData.get('isActive') === 'on'
                  });
                } else if (editType === 'department') {
                  updateDepartment(editingItem._id, {
                    name: formData.get('name'),
                    description: formData.get('description'),
                    isActive: formData.get('isActive') === 'on'
                  });
                } else {
                  updatePosition(editingItem._id, {
                    name: formData.get('name'),
                    description: formData.get('description'),
                    isActive: formData.get('isActive') === 'on'
                  });
                }
              }} className="p-6 space-y-4">
                <FormInput label="Name" name="name" defaultValue={editingItem.name} required />

                {editType === 'shift' && (
                  <>
                    <div className="grid grid-cols-2 gap-4">
                      <FormInput label="Start Time" name="startTime" type="time" defaultValue={editingItem.startTime} required />
                      <FormInput label="End Time" name="endTime" type="time" defaultValue={editingItem.endTime} required />
                    </div>
                    <FormInput label="Late Grace (mins)" name="lateGraceMinutes" type="number" defaultValue={editingItem.lateGraceMinutes} />
                  </>
                )}

                {(editType === 'department' || editType === 'position') && (
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1.5">Description</label>
                    <textarea name="description" defaultValue={editingItem.description || ''} rows="3" className="w-full px-3 py-2.5 border border-slate-200 rounded-md focus:ring-2 focus:ring-gray-200 focus:border-transparent text-sm resize-none" />
                  </div>
                )}

                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" name="isActive" defaultChecked={editingItem.isActive} className="w-4 h-4 rounded text-gray-700" />
                  <span className="text-sm text-slate-700">Active</span>
                </label>

                <div className="flex gap-3 pt-2">
                  <button type="button" onClick={() => setShowEditModal(false)} className="flex-1 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 font-medium rounded-md transition-colors">
                    Cancel
                  </button>
                  <button type="submit" className="flex-1 py-2.5 bg-gray-900 hover:bg-gray-800 text-white font-semibold rounded-md transition-colors">
                    Save Changes
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// Helper Components
const FormInput = ({ label, name, type = 'text', value, defaultValue, onChange, required = false }) => (
  <div>
    <label className="block text-xs font-medium text-gray-600 mb-1.5">{label}</label>
    <input
      type={type}
      name={name}
      value={value}
      defaultValue={defaultValue}
      onChange={onChange}
      required={required}
      className="w-full px-3 py-2.5 border border-slate-200 rounded-md focus:ring-2 focus:ring-gray-200 focus:border-transparent text-sm"
    />
  </div>
);

const ItemRow = ({ title, subtitle, isActive, onToggle, onEdit, onDelete, color }) => (
  <div className="px-4 py-3 flex items-center justify-between hover:bg-slate-50/50 transition-colors">
    <div className="flex items-center gap-3">
      <div className={`w-10 h-10 rounded-md bg-gray-100 flex items-center justify-center`}>
        <span className={`text-sm font-bold text-gray-700`}>{title?.[0]}</span>
      </div>
      <div>
        <div className="text-sm font-semibold text-slate-800">{title}</div>
        <div className="text-xs text-slate-500">{subtitle}</div>
      </div>
    </div>
    <div className="flex items-center gap-2">
      <button
        onClick={onToggle}
        className={`p-1.5 rounded-lg transition-colors ${isActive ? 'text-gray-700 hover:bg-gray-100' : 'text-slate-400 hover:bg-slate-100'}`}
        title={isActive ? 'Disable' : 'Enable'}
      >
        {isActive ? <ToggleRight size={20} /> : <ToggleLeft size={20} />}
      </button>
      <button onClick={onEdit} className="p-1.5 text-slate-400 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors">
        <Edit2 size={16} />
      </button>
      <button onClick={onDelete} className="p-1.5 text-slate-400 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors">
        <Trash2 size={16} />
      </button>
    </div>
  </div>
);

export default Settings;
